import React, { useEffect, useState } from "react";
import { Button, Card, Col, Form, Input, message, Modal, Popconfirm, Row, Space, Table, Tabs, Upload } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined, UploadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import ImgCrop from "antd-img-crop";
import { useProductColors } from "../../../../data/hooks/useProductColors";
import { useProductMaterials } from "../../../../data/hooks/useProductMaterials";

const { TabPane } = Tabs;

export default function ProductAttributes() {
  const { t } = useTranslation();
  const { colors, addColor, updateColor, deleteColor, isLoading: colorsLoading } = useProductColors();
  const { materials, addMaterial, updateMaterial, deleteMaterial, isLoading: materialsLoading } = useProductMaterials();

  const [colorForm] = Form.useForm();
  const [materialForm] = Form.useForm();
  const [editColorForm] = Form.useForm();
  const [editMaterialForm] = Form.useForm();

  const [colorModalOpen, setColorModalOpen] = useState(false);
  const [materialModalOpen, setMaterialModalOpen] = useState(false);
  const [editColorModalOpen, setEditColorModalOpen] = useState(false);
  const [editMaterialModalOpen, setEditMaterialModalOpen] = useState(false);

  const [currentColor, setCurrentColor] = useState(null);
  const [currentMaterial, setCurrentMaterial] = useState(null);

  const [colorImageFile, setColorImageFile] = useState(null);
  const [editColorImageFile, setEditColorImageFile] = useState(null);

  const [messageApi, contextHolder] = message.useMessage();

  const handleCreateColor = () => {
    colorForm.validateFields().then(async (values) => {
      try {
        await addColor({
          name: values.name,
          code: values.code.toUpperCase(),
          imageFile: colorImageFile,
          active: true,
        });
        setColorModalOpen(false);
        colorForm.resetFields();
        setColorImageFile(null);
      } catch (error) {
        messageApi.error(error.message);
      }
    });
  };

  const handleUpdateColor = () => {
    editColorForm.validateFields().then(async (values) => {
      try {
        await updateColor(currentColor.id, {
          name: values.name,
          code: values.code.toUpperCase(),
          imageFile: editColorImageFile,
        });
        setEditColorModalOpen(false);
        setCurrentColor(null);
        editColorForm.resetFields();
        setEditColorImageFile(null);
      } catch (error) {
        messageApi.error(error.message);
      }
    });
  };

  const handleDeleteColor = async (color) => {
    try {
      await deleteColor(color.id);
    } catch (error) {
      messageApi.error(error.message);
    }
  };

  const handleCreateMaterial = () => {
    materialForm.validateFields().then(async (values) => {
      try {
        await addMaterial({
          name: values.name,
          code: values.code.toUpperCase(),
          description: values.description || null,
          active: true,
        });
        setMaterialModalOpen(false);
        materialForm.resetFields();
      } catch (error) {
        messageApi.error(error.message);
      }
    });
  };

  const handleUpdateMaterial = () => {
    editMaterialForm.validateFields().then(async (values) => {
      try {
        await updateMaterial(currentMaterial.id, {
          name: values.name,
          code: values.code.toUpperCase(),
          description: values.description || null,
        });
        setEditMaterialModalOpen(false);
        setCurrentMaterial(null);
        editMaterialForm.resetFields();
      } catch (error) {
        messageApi.error(error.message);
      }
    });
  };

  const handleDeleteMaterial = async (material) => {
    try {
      await deleteMaterial(material.id);
    } catch (error) {
      messageApi.error(error.message);
    }
  };

  const openEditColorModal = (color) => {
    setCurrentColor(color);
    editColorForm.setFieldsValue({
      name: color.name,
      code: color.code,
    });
    setEditColorImageFile(null);
    setEditColorModalOpen(true);
  };

  const openEditMaterialModal = (material) => {
    setCurrentMaterial(material);
    editMaterialForm.setFieldsValue({
      name: material.name,
      code: material.code,
      description: material.description,
    });
    setEditMaterialModalOpen(true);
  };

  const colorImageUploadProps = {
    name: "file",
    multiple: false,
    listType: "picture-card",
    maxCount: 1,
    beforeUpload: (file) => {
      setColorImageFile(file);
      return false; // Prevent automatic upload
    },
    onRemove: () => {
      setColorImageFile(null);
    },
  };

  const editColorImageUploadProps = {
    name: "file",
    multiple: false,
    listType: "picture-card",
    maxCount: 1,
    defaultFileList: currentColor?.image
      ? [
          {
            uid: "-1",
            name: "current-image",
            status: "done",
            url: currentColor.image,
          },
        ]
      : [],
    beforeUpload: (file) => {
      setEditColorImageFile(file);
      return false; // Prevent automatic upload
    },
    onRemove: () => {
      setEditColorImageFile(null);
    },
  };

  const colorColumns = [
    {
      title: t("image"),
      dataIndex: "image",
      key: "image",
      render: (image) =>
        image ? (
          <img src={image} alt="color" style={{ width: 50, height: 50, objectFit: "cover", borderRadius: 4 }} />
        ) : (
          <div style={{ width: 50, height: 50, background: "#f0f0f0", borderRadius: 4 }} />
        ),
    },
    {
      title: t("name"),
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      defaultSortOrder: "ascend",
    },
    {
      title: t("code"),
      dataIndex: "code",
      key: "code",
    },
    {
      title: t("actions"),
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button type="text" icon={<EditOutlined />} onClick={() => openEditColorModal(record)} />
          <Popconfirm
            title={t("confirm_delete_color")}
            onConfirm={() => handleDeleteColor(record)}
            okText={t("yes")}
            cancelText={t("no")}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const materialColumns = [
    {
      title: t("name"),
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      defaultSortOrder: "ascend",
    },
    {
      title: t("code"),
      dataIndex: "code",
      key: "code",
    },
    {
      title: t("description"),
      dataIndex: "description",
      key: "description",
    },
    {
      title: t("actions"),
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button type="text" icon={<EditOutlined />} onClick={() => openEditMaterialModal(record)} />
          <Popconfirm
            title={t("confirm_delete_material")}
            onConfirm={() => handleDeleteMaterial(record)}
            okText={t("yes")}
            cancelText={t("no")}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      <Card>
        <Tabs defaultActiveKey="colors">
          <TabPane tab={t("colors")} key="colors">
            <div style={{ marginBottom: 16 }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setColorModalOpen(true)}>
                {t("add_color")}
              </Button>
            </div>
            <Table columns={colorColumns} dataSource={colors || []} rowKey="id" pagination={{ pageSize: 20 }} />
          </TabPane>
          <TabPane tab={t("materials")} key="materials">
            <div style={{ marginBottom: 16 }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setMaterialModalOpen(true)}>
                {t("add_material")}
              </Button>
            </div>
            <Table columns={materialColumns} dataSource={materials || []} rowKey="id" pagination={{ pageSize: 20 }} />
          </TabPane>
        </Tabs>
      </Card>

      <Modal
        title={t("add_color")}
        open={colorModalOpen}
        onOk={handleCreateColor}
        onCancel={() => {
          setColorModalOpen(false);
          colorForm.resetFields();
          setColorImageFile(null);
        }}
        okText={t("add")}
        cancelText={t("cancel")}
      >
        <Form form={colorForm} layout="vertical">
          <Form.Item name="name" label={t("name")} rules={[{ required: true, message: t("form.enter_name") }]}>
            <Input />
          </Form.Item>
          <Form.Item name="code" label={t("code")} rules={[{ required: true, message: t("form.enter_code") }]}>
            <Input placeholder="BLACK" />
          </Form.Item>
          <Form.Item label={t("image")}>
            <ImgCrop aspect={1} quality={1}>
              <Upload {...colorImageUploadProps}>
                {!colorImageFile && (
                  <div>
                    <PlusOutlined />
                    <div style={{ marginTop: 8 }}>{t("upload")}</div>
                  </div>
                )}
              </Upload>
            </ImgCrop>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t("edit_color")}
        open={editColorModalOpen}
        onOk={handleUpdateColor}
        onCancel={() => {
          setEditColorModalOpen(false);
          setCurrentColor(null);
          editColorForm.resetFields();
          setEditColorImageFile(null);
        }}
        okText={t("save")}
        cancelText={t("cancel")}
      >
        <Form form={editColorForm} layout="vertical">
          <Form.Item name="name" label={t("name")} rules={[{ required: true, message: t("form.enter_name") }]}>
            <Input />
          </Form.Item>
          <Form.Item name="code" label={t("code")} rules={[{ required: true, message: t("form.enter_code") }]}>
            <Input placeholder="BLACK" />
          </Form.Item>
          <Form.Item label={t("image")}>
            <ImgCrop aspect={1} quality={1}>
              <Upload {...editColorImageUploadProps}>
                {!editColorImageFile && !currentColor?.image && (
                  <div>
                    <PlusOutlined />
                    <div style={{ marginTop: 8 }}>{t("upload")}</div>
                  </div>
                )}
              </Upload>
            </ImgCrop>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t("add_material")}
        open={materialModalOpen}
        onOk={handleCreateMaterial}
        onCancel={() => {
          setMaterialModalOpen(false);
          materialForm.resetFields();
        }}
        okText={t("add")}
        cancelText={t("cancel")}
      >
        <Form form={materialForm} layout="vertical">
          <Form.Item name="name" label={t("name")} rules={[{ required: true, message: t("form.enter_name") }]}>
            <Input />
          </Form.Item>
          <Form.Item name="code" label={t("code")} rules={[{ required: true, message: t("form.enter_code") }]}>
            <Input placeholder="GLASS" />
          </Form.Item>
          <Form.Item name="description" label={t("description")}>
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t("edit_material")}
        open={editMaterialModalOpen}
        onOk={handleUpdateMaterial}
        onCancel={() => {
          setEditMaterialModalOpen(false);
          setCurrentMaterial(null);
          editMaterialForm.resetFields();
        }}
        okText={t("save")}
        cancelText={t("cancel")}
      >
        <Form form={editMaterialForm} layout="vertical">
          <Form.Item name="name" label={t("name")} rules={[{ required: true, message: t("form.enter_name") }]}>
            <Input />
          </Form.Item>
          <Form.Item name="code" label={t("code")} rules={[{ required: true, message: t("form.enter_code") }]}>
            <Input placeholder="GLASS" />
          </Form.Item>
          <Form.Item name="description" label={t("description")}>
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
